import type { NoteDto } from '@keep/shared';
import { AlarmClock, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useLabels } from '../../api/labels';
import { cn } from '../../lib/cn';
import { formatFull, formatReminder } from '../../lib/dates';
import { paths } from '../../lib/routes';

function Chip({
  children,
  onClick,
  onRemove,
  removeLabel,
  title,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  onRemove?: () => void;
  removeLabel?: string;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'group/chip relative inline-flex h-6 max-w-full items-center gap-1 rounded-full bg-chip px-2.5 text-[11px] leading-none',
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={(event) => {
        if (!onClick) return;
        event.stopPropagation();
        onClick();
      }}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="absolute right-0.5 hidden size-5 items-center justify-center rounded-full bg-chip group-hover/chip:flex hover:bg-hover-strong"
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
}

/** Pastilles « rappel » et « libellés » sous le contenu d'une note. */
export function NoteChips({
  note,
  onRemoveReminder,
  onRemoveLabel,
  className,
}: {
  note: Pick<NoteDto, 'reminderAt' | 'labelIds'>;
  onRemoveReminder?: () => void;
  onRemoveLabel?: (labelId: string) => void;
  className?: string;
}) {
  const { data: labels = [] } = useLabels();
  const navigate = useNavigate();
  const noteLabels = labels.filter((l) => note.labelIds.includes(l.id));
  if (!note.reminderAt && noteLabels.length === 0) return null;
  const overdue = note.reminderAt ? new Date(note.reminderAt).getTime() < Date.now() : false;

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {note.reminderAt && (
        <Chip
          title={formatFull(note.reminderAt)}
          onRemove={onRemoveReminder}
          removeLabel="Supprimer le rappel"
          className={cn(overdue && 'line-through decoration-1')}
        >
          <AlarmClock size={14} aria-hidden className="shrink-0" />
          <span className="truncate">{formatReminder(note.reminderAt)}</span>
        </Chip>
      )}
      {noteLabels.map((label) => (
        <Chip
          key={label.id}
          onClick={() => navigate(paths.label(label.id))}
          onRemove={onRemoveLabel ? () => onRemoveLabel(label.id) : undefined}
          removeLabel={`Retirer le libellé ${label.name}`}
        >
          <span className="truncate">{label.name}</span>
        </Chip>
      ))}
    </div>
  );
}

/** Pastilles rondes des collaborateurs (initiales). */
export function CollaboratorAvatars({ note, onClick }: { note: NoteDto; onClick?: () => void }) {
  if (note.collaborators.length === 0) return null;
  const people = [note.owner, ...note.collaborators];
  return (
    <div className="flex flex-wrap gap-1" onClick={onClick}>
      {people.map((person) => (
        <span
          key={person.id}
          title={`${person.name} (${person.email})`}
          className="flex size-7 items-center justify-center rounded-full border border-line bg-[#7b1fa2] text-xs font-medium text-white"
        >
          {(person.name || person.email).charAt(0).toUpperCase()}
        </span>
      ))}
    </div>
  );
}
